/**
 * Creates a debounced version of a function. The debounced function will only
 * execute once after a specified wait period has passed since the last time
 * the debounced function was invoked. This prevents multiple rapid calls to
 * the function, for example, when typing quickly or resizing a GUI window.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @param {boolean} [immediate=false] - If true, the function will be invoked
 *        on the leading edge of the wait period (instead of the trailing edge).
 * @returns {Function} A debounced version of the provided function.
 *
 * @example
 * const debouncedLog = debounce(1000, function() {
 *   console.log('Executed after delay');
 * });
 *
 * This will only call the function once after 1000ms, even though
 * it might be invoked multiple times in rapid succession.
 */
export function debounce(wait, func, immediate = false) {
  let timeout;

  return function debouncedFunction(...args) {
    const context = this;
    const later = () => {
      timeout = undefined;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func.apply(context, args);
  };
}
